/* eslint-disable react/no-unescaped-entities */
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    Autocomplete,
    Box,
    CircularProgress,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import PrimaryButton from 'components/buttons/PrimaryButton';
import AppTextField from 'components/input-fields/AppTextField';
import { useFormik } from 'formik';
import useAuth from 'hooks/useAuth';
import useBoolean from 'hooks/useBoolean';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import * as yup from 'yup';
import { VideoPlayer } from '../../../SigninCover/components';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

const phoneRegExp =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = yup.object({
    fullName: yup
        .string()
        .trim()
        .min(2, 'Please enter a valid name')
        .max(50, 'Please enter a valid name')
        .required('Please specify your full name'),
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
    perkiId: yup.string(),
    nik: yup.string().when('registerType', {
        is: 'individu',
        then: yup
            .string()
            .trim()
            .min(16, 'Please enter a valid NIK')
            .max(16, 'Please enter a valid NIK')
            .required('Please specify your NIK'),
        otherwise: yup.string().trim(),
    }),
    registerType: yup.string().required('Please specify your register type'),
    phoneNumber: yup
        .string()
        .matches(phoneRegExp, 'Phone number is not valid')
        .trim()
        .min(9, 'Please enter a valid phone number')
        .max(14, 'Please enter a valid phone number')
        .required('Please specify your phone number'),
    institution: yup
        .string()
        .trim()
        .min(2, 'Please enter a valid institution')
        .max(50, 'Please enter a valid institution')
        .required('Please specify your institution'),
    profession: yup.string().when('registerType', {
        is: 'individu',
        then: yup
            .string()
            .trim()
            .min(2, 'Please select a profession')
            .max(50, 'Please select a profession')
            .required('Please specify your profession'),
        otherwise: yup.string().trim(),
    }),
    city: yup
        .string()
        .trim()
        .min(2, 'Please enter a valid city')
        .max(50, 'Please enter a valid city')
        .required('Please specify your city'),
    country: yup
        .string()
        .trim()
        .min(2, 'Please select a country')
        .max(50, 'Please select a country')
        .required('Please specify your country'),
    sponsor: yup.string().when('registerType', {
        is: 'group',
        then: yup
            .string()
            .trim()
            .required('* Sponsor is required for group registration'),
        otherwise: yup.string().trim(),
    }),
    password: yup
        .string()
        .required('Please specify your password')
        .max(30, 'The password should have at maximum length of 30')
        .min(6, 'The password should have at minimum length of 6'),
    confirmPassword: yup
        .string()
        .oneOf(
            [yup.ref('password'), null],
            'Passwords and confirm password must match',
        )
        .required('Please specify your password confirmation')
        .min(6, 'The password should have at minimum length of 6'),
});

const RegisterTypeOptions = [
    {
        value: 'individu',
        label: 'Individual',
    },
    {
        value: 'group',
        label: 'Group',
    },
];

const Form = () => {
    const [error, setError] = useState('');

    const [loading, setLoading] = useState(false);

    const [professionList, setProfessionList] = useState([]);

    const [countryList, setCountryList] = useState([]);

    const [sponsorList, setSponsorList] = useState([]);

    const { register } = useAuth();

    const navigate = useNavigate();

    const showPassword = useBoolean();

    const showConfirmPassword = useBoolean();

    const TutorialVideoIds = {
        individu: '8ce3330ff22c33d178270ec0b36d8987',
        group: 'c66728095b8373ee46c9f6df00557d79',
    };

    const [videoType, setVideoType] = useState('individu');

    const initialValues = {
        fullName: '',
        registerType: '',
        email: '',
        nik: '',
        phoneNumber: '',
        institution: '',
        profession: '',
        perkiId: '',
        city: '',
        country: '',
        sponsor: '',
        password: '',
        confirmPassword: '',
    };

    const getData = async () => {
        await axiosInstance
            .get('/api/profession')
            .then((res) => setProfessionList(res.data))
            .catch((err) => console.log(err));

        await axiosInstance
            .get('/api/country')
            .then((res) => setCountryList(res.data))
            .catch((err) => console.log(err));

        await axiosInstance
            .get('/api/sponsor')
            .then((res) => setSponsorList(res.data))
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        getData();
    }, []);

    const onSubmit = async (values) => {
        setLoading(true);
        setError(false);

        await register(values)
            .then((res) => {
                toast.success('You Register Successfully');

                if (res.user.role === 'member') {
                    navigate('/dashboard');
                }

                if (res.user.role === 'group') {
                    navigate('/dashboard/group-registration');
                }
            })
            .catch((error) => {
                if (
                    error.message ===
                    `Cannot read properties of undefined (reading 'id')`
                ) {
                    setError(
                        'This email address already registered. Please use another email address or login with this email address.',
                    );
                } else {
                    setError(error.message);
                }
            })
            .finally(() => setLoading(false));
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit,
    });

    const SignInInfo = ({ align = 'flex-start' }) => (
        <Box display="flex" justifyContent={align}>
            <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                    to={'/signin'}
                    style={{ textDecoration: 'none' }}
                    color="primary"
                >
                    Sign in
                </Link>
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Box marginBottom={4}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                    }}
                >
                    Create an account
                </Typography>

                <Typography color="text.secondary" align="center" mb={2}>
                    Choose which tutorial to watch: <strong>Individual</strong>{' '}
                    or <strong>Group</strong>
                </Typography>
                <Box sx={{ my: 3 }}>
                    <Box display="flex" justifyContent="center" mb={1}>
                        <ToggleButtonGroup
                            size="small"
                            value={videoType}
                            exclusive
                            onChange={(_, value) => {
                                if (value) setVideoType(value);
                            }}
                        >
                            <ToggleButton value="individu">
                                Individual
                            </ToggleButton>
                            <ToggleButton value="group">Group</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box display="flex" justifyContent="center" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                            Video Tutorial:
                        </Typography>
                    </Box>
                    <VideoPlayer
                        key={TutorialVideoIds[videoType]}
                        frame={false}
                        aspect={'16/9'}
                        videoId={
                            TutorialVideoIds[videoType] ||
                            TutorialVideoIds.individu
                        }
                        muted
                        autoplay={false}
                    />
                </Box>

                <SignInInfo align="center" />
            </Box>

            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Registration Type
                        </Typography>
                        <Autocomplete
                            disablePortal
                            options={RegisterTypeOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                            }
                            value={
                                RegisterTypeOptions.find(
                                    (o) =>
                                        o.value === formik.values.registerType,
                                ) || null
                            }
                            onChange={(_, value) => {
                                formik.setFieldValue(
                                    'registerType',
                                    value?.value || '',
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Type"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                        },
                                    }}
                                />
                            )}
                        />
                        {formik.touched.registerType && (
                            <FormHelperText
                                error
                                sx={{
                                    ml: '14px',
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                }}
                            >
                                {formik.errors.registerType}
                            </FormHelperText>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your full name
                        </Typography>
                        <AppTextField
                            label="Full name *"
                            variant="outlined"
                            name={'fullName'}
                            fullWidth
                            value={formik.values.fullName}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.fullName &&
                                Boolean(formik.errors.fullName)
                            }
                            helperText={
                                formik.touched.fullName &&
                                formik.errors.fullName
                            }
                        />
                    </Grid>

                    {formik.values.registerType !== 'group' && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant={'subtitle2'}
                                    sx={{ marginBottom: 2 }}
                                >
                                    Select your profession
                                </Typography>
                                <Autocomplete
                                    disablePortal
                                    options={professionList}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={(_, value) => {
                                        formik.setFieldValue(
                                            'profession',
                                            value?.id || '',
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Profession"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                },
                                            }}
                                        />
                                    )}
                                />
                                {formik.touched.profession && (
                                    <FormHelperText
                                        error
                                        sx={{
                                            ml: '14px',
                                            fontSize: '0.75rem',
                                            fontWeight: 400,
                                        }}
                                    >
                                        {formik.errors.profession}
                                    </FormHelperText>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant={'subtitle2'}
                                    sx={{ marginBottom: 2 }}
                                >
                                    Perki ID
                                </Typography>

                                <AppTextField
                                    label="Perki ID"
                                    variant="outlined"
                                    name={'perkiId'}
                                    fullWidth
                                    value={formik.values.perkiId}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.perkiId &&
                                        Boolean(formik.errors.perkiId)
                                    }
                                    helperText={
                                        formik.touched.perkiId &&
                                        formik.errors.perkiId
                                    }
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your Institution / Hospital
                        </Typography>

                        <AppTextField
                            label="Institution / Hospital *"
                            variant="outlined"
                            name={'institution'}
                            fullWidth
                            value={formik.values.institution}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.institution &&
                                Boolean(formik.errors.institution)
                            }
                            helperText={
                                formik.touched.institution &&
                                formik.errors.institution
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your city
                        </Typography>

                        <AppTextField
                            label="City *"
                            variant="outlined"
                            name={'city'}
                            fullWidth
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.city &&
                                Boolean(formik.errors.city)
                            }
                            helperText={
                                formik.touched.city && formik.errors.city
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Select your country
                        </Typography>

                        <Autocomplete
                            disablePortal
                            options={countryList}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            onChange={(_, value) => {
                                formik.setFieldValue(
                                    'country',
                                    value?.id || '',
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Country"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                        },
                                    }}
                                />
                            )}
                        />
                        {formik.touched.country && (
                            <FormHelperText
                                error
                                sx={{
                                    ml: '14px',
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                }}
                            >
                                {formik.errors.country}
                            </FormHelperText>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Select your sponsor
                        </Typography>

                        <Autocomplete
                            disablePortal
                            options={sponsorList}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            onChange={(_, value) => {
                                formik.setFieldValue(
                                    'sponsor',
                                    value?.id || '',
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Sponsor"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                        },
                                    }}
                                />
                            )}
                        />
                        {formik.touched.sponsor && (
                            <FormHelperText
                                error
                                sx={{
                                    ml: '14px',
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                }}
                            >
                                {formik.errors.sponsor}
                            </FormHelperText>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your phone number
                        </Typography>

                        <AppTextField
                            label="Phone number *"
                            variant="outlined"
                            name={'phoneNumber'}
                            fullWidth
                            value={formik.values.phoneNumber}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.phoneNumber &&
                                Boolean(formik.errors.phoneNumber)
                            }
                            helperText={
                                formik.touched.phoneNumber &&
                                formik.errors.phoneNumber
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your email
                        </Typography>

                        <AppTextField
                            label="Email *"
                            variant="outlined"
                            name={'email'}
                            fullWidth
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                            }
                            helperText={
                                formik.touched.email && formik.errors.email
                            }
                        />
                    </Grid>

                    {formik.values.registerType !== 'group' && (
                        <Grid item xs={12} sm={12}>
                            <Typography
                                variant={'subtitle2'}
                                sx={{ marginBottom: 2 }}
                            >
                                Enter your NIK
                            </Typography>

                            <AppTextField
                                label="NIK *"
                                variant="outlined"
                                name={'nik'}
                                fullWidth
                                value={formik.values.nik}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.nik &&
                                    Boolean(formik.errors.nik)
                                }
                                helperText={
                                    formik.touched.nik && formik.errors.nik
                                }
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Enter your password
                        </Typography>

                        <AppTextField
                            label="Password *"
                            variant="outlined"
                            name={'password'}
                            type={showPassword.value ? 'text' : 'password'}
                            fullWidth
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.password &&
                                Boolean(formik.errors.password)
                            }
                            helperText={
                                formik.touched.password &&
                                formik.errors.password
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                showPassword.onToggle()
                                            }
                                            edge="end"
                                        >
                                            {showPassword.value ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{ marginBottom: 2 }}
                        >
                            Confirm your password
                        </Typography>

                        <AppTextField
                            label="Confirm Password *"
                            variant="outlined"
                            name={'confirmPassword'}
                            type={
                                showConfirmPassword.value ? 'text' : 'password'
                            }
                            fullWidth
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.confirmPassword &&
                                Boolean(formik.errors.confirmPassword)
                            }
                            helperText={
                                formik.touched.confirmPassword &&
                                formik.errors.confirmPassword
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                showConfirmPassword.onToggle()
                                            }
                                            edge="end"
                                        >
                                            {showConfirmPassword.value ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {error && (
                        <FormHelperText
                            error
                            sx={{
                                mt: 2,
                                ml: 4,
                                fontSize: 13,
                                fontWeight: 500,
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </FormHelperText>
                    )}

                    <Grid item container xs={12}>
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            width={1}
                            maxWidth={600}
                            margin={'0 auto'}
                        >
                            <PrimaryButton
                                size={'large'}
                                variant={'contained'}
                                type={'submit'}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        Loading . . .
                                        <CircularProgress size={20} />
                                    </>
                                ) : (
                                    <>
                                        <AppRegistrationIcon /> &nbsp; Sign Up
                                    </>
                                )}
                            </PrimaryButton>
                            <SignInInfo />
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default Form;
